import { getEase, Tween } from "@westpierstudio/luma/createjs";
import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { CoreSymbolID, JackpotWedgeID } from "servertypes/SharedTypes";
import { PositionList } from "./WheelPrizePanelModel";
import { Signal } from "type-signals";
import { Spine } from "pixi-spine";
import { addToSpineSlot, resetSpine } from "utils/Misc";
import { Sprite, Container, Graphics, Text, TextMetrics, TextStyle } from "pixi.js";
import { BoundedContainer } from "utils/BoundedContainer";

enum PanelConfig {
    CASH,
    FREE_SPINS,
    EXTEND_FREE_SPINS,
    JACKPOT,
    SYMBOLS_TWO,
    SYMBOLS_SIX
}

type PrizeText = {
    cashPanel: {
        line1Value: Text,
        line1ValueBounds: BoundedContainer,
        line2Text: Text
    }
    freeSpinsPanel: {
        line1Text: Text,
        line2Value: Text,
        line2Text: Text,
        line3TextA: Text,
        line3Value: Text,
        line3ValueBounds: BoundedContainer,
        line3TextB: Text
    },
    extendFreeSpinsPanel: {
        line1Text: Text,
        line2Value: Text,
        line2Text: Text,
        line3TextA: Text,
        line3Value: Text,
        line3ValueBounds: BoundedContainer,
        line3TextB: Text
    },
    jackpotPanel: {
        bounds: BoundedContainer
        value: Text
    },
    symbolPanel: {
        line1Text: Text,
        line1Separators: Text[],
        line2Text: Text,
        line3TextA: Text,
        line3Value: Text,
        line3ValueBounds: BoundedContainer,
        line3TextB: Text
    }
}

class WheelPrizePanelSignals {
    startSymbolCollection = new Signal();
    startUICountUp = new Signal();
    prizeSequenceComplete = new Signal();
    panelMoveStarted = new Signal();
}

export class WheelPrizePanel extends View {
    private readonly spec: Record<string, any>;
    private readonly panel: Sprite;
    private readonly prizeText: PrizeText;
    private readonly jackpotWinBox: Spine;
    private readonly symbolIDs: CoreSymbolID[] = [`M1`, `M2`, `M3`, `F4`, `F5`, `F6`];
    private symbols: Map<CoreSymbolID, Spine>;
    private alphaFadeTextContainer!: Container;
    private scaledTextContainer!: BoundedContainer;
    private maskGraphic!: Graphics;
    jackpotBowlContainer: Container;
    signals = new WheelPrizePanelSignals();

    constructor(override services: GameServices) {
        super(services);

        const {factory, resources} = this.services;

        this.spec = resources.getSpec(`wheelprizepanel`);
        factory.applySpec(this, this.spec);

        this.panel = factory.createSprite(this.spec.prizePanelBackground, this);
        this.prizeText = this.setupText(this.spec);
        this.symbols = this.setupSymbols();

        this.jackpotBowlContainer = factory.createContainer(this.spec.jackpotPanel.jackpotBowlContainer, this);

        this.jackpotWinBox = factory.createSpine(this.spec.jackpotPanel.jackpotWinBox, this);
        this.jackpotWinBox.visible = false;
        addToSpineSlot(this.jackpotWinBox, this.prizeText.jackpotPanel.bounds, `dynamic-text`);

        this.setupMask();

        this.visible = false;
    }

    showCashPanel(money: number): void {

        this.prizeText.cashPanel.line1Value.text = this.services.currency.formatCoins(money, {group: true});
        this.prizeText.cashPanel.line1ValueBounds.update();

        this.configureScaledTextContainer(PanelConfig.CASH);
        this.playScaledTextAnim(() => this.signals.startUICountUp.dispatch());
    }

    showFreeSpinsPanel(freeSpins: number, money: number): void {

        const {line2Text, line2Value, line3Value, line3ValueBounds, line3TextA, line3TextB} = this.prizeText.freeSpinsPanel;

        line2Value.text = freeSpins;
        line3Value.text = this.services.currency.formatCoins(money, {group: true});
        line3ValueBounds.update();

        this.arrangeLine([line2Value, line2Text], TextMetrics.measureText(` `, line2Text.style as TextStyle));
        this.arrangeLine([line3TextA, line3ValueBounds, line3TextB], TextMetrics.measureText(` `, line3TextA.style as TextStyle));

        this.configureScaledTextContainer(PanelConfig.FREE_SPINS);
        this.playScaledTextAnim(() => this.signals.startUICountUp.dispatch());
    }

    showExtendFreeSpinsPanel(freeSpins: number, money: number): void {

        const {line2Text, line2Value, line3Value, line3ValueBounds, line3TextA, line3TextB} = this.prizeText.extendFreeSpinsPanel;

        line2Value.text = freeSpins;
        line3Value.text = this.services.currency.formatCoins(money, {group: true});
        line3ValueBounds.update();

        this.arrangeLine([line2Value, line2Text], TextMetrics.measureText(` `, line2Text.style as TextStyle));
        this.arrangeLine([line3TextA, line3ValueBounds, line3TextB], TextMetrics.measureText(` `, line3TextA.style as TextStyle));

        this.configureScaledTextContainer(PanelConfig.EXTEND_FREE_SPINS);
        this.playScaledTextAnim(() => this.signals.startUICountUp.dispatch());
    }

    showJackpotPanel(money: number, winningWedge: JackpotWedgeID): void {

        this.prizeText.jackpotPanel.value.text = this.services.currency.formatCoins(money, {group: true});
        this.prizeText.jackpotPanel.bounds.update();

        if (winningWedge === JackpotWedgeID.JACKPOT_0) {

            this.prizeText.jackpotPanel.value.style = this.spec.jackpotPanel.styles.major;
            this.jackpotWinBox.skeleton.setSkinByName(`major-label`);

        } else if (winningWedge === JackpotWedgeID.JACKPOT_1) {

            this.prizeText.jackpotPanel.value.style = this.spec.jackpotPanel.styles.minor;
            this.jackpotWinBox.skeleton.setSkinByName(`minor-label`);

        } else {

            this.prizeText.jackpotPanel.value.style = this.spec.jackpotPanel.styles.mini;
            this.jackpotWinBox.skeleton.setSkinByName(`mini-label`);
        }

        Tween.get(this)
            .wait(750)
            .call(() => {
                this.jackpotWinBox.state.setAnimation(0, `intro`, false);
                this.jackpotWinBox.state.addAnimation(0, `loop`, true, 0);
                this.jackpotWinBox.visible = true;
            });
    }

    showSymbolPanel(symbols: CoreSymbolID[], money: number): void {

        const settings = this.services.resources.getSetting(`wheelPrizePanel`).symbolPanel;

        let initalPositions: PositionList, finalPositions: PositionList;
        let initialScale: number, finalScale: number;

        const {line3TextA, line3Value, line3ValueBounds, line3TextB} = this.prizeText.symbolPanel;

        line3Value.text = this.services.currency.formatCoins(money, {group: true});
        line3ValueBounds.update();

        this.arrangeLine([line3TextA, line3ValueBounds, line3TextB], TextMetrics.measureText(` `, line3TextA.style as TextStyle));

        if (symbols.length === 2) {
            initalPositions = this.spec.symbolPanel.symbolPositions.two.initial;
            finalPositions = this.spec.symbolPanel.symbolPositions.two.final;
            initialScale = settings.symbols.initialScale.two;
            finalScale = settings.symbols.finalScale.two;

            this.configureAlphaFadeTextContainer(PanelConfig.SYMBOLS_TWO);
            this.configureScaledTextContainer(PanelConfig.SYMBOLS_TWO);

        } else {

            initalPositions = this.spec.symbolPanel.symbolPositions.six.initial;
            finalPositions = this.spec.symbolPanel.symbolPositions.six.final;
            initialScale = settings.symbols.initialScale.six;
            finalScale = settings.symbols.finalScale.six;

            this.configureAlphaFadeTextContainer(PanelConfig.SYMBOLS_SIX);
            this.configureScaledTextContainer(PanelConfig.SYMBOLS_SIX);
        }

        let spine;

        for (const [i, symbol] of symbols.entries()) {

            spine = this.symbols.get(symbol) as (Spine);

            spine.x = initalPositions[i].x;
            spine.y = initalPositions[i].y;
            spine.alpha = 0;
            spine.scale.x = initialScale;
            spine.scale.y = initialScale;
            spine.visible = true;

            this.startSymbolAnim(
                this.symbols.get(symbol) as (Spine),
                finalPositions[i].x,
                finalPositions[i].y,
                finalScale,
                (i === symbols.length -1)
            );
        }
    }

    arrangeLine(items: (Text | BoundedContainer)[], space: TextMetrics) {
        const lineWidth = items.reduce((_width, item) => _width + item.width, 0) + (space.width * Math.max(0, (items.length - 1)));
        let x = -lineWidth / 2;
        items.forEach((item, i) => {
            item.x = x;
            x += item.width + space.width;
        });
    }

    hideSymbol(symbolID: CoreSymbolID): void {

        const spine = this.symbols.get(symbolID) as (Spine);
        spine.visible = false;
    }

    hidePanel(): void {

        const settings = this.services.resources.getSetting(`wheelPrizePanel`);

        const holdTime = this.jackpotWinBox.visible ? settings.jackpotPanelHoldTime : settings.panelHoldTime;

        this.enableMask();

        Tween.get(this.panel)
        .wait(holdTime)
        .call(() => {
            if (this.jackpotWinBox.visible) {
                this.jackpotWinBox.state.setAnimation(0, `outro`, false);
            }
            this.signals.panelMoveStarted.dispatch();
        })
        .to({x: 0, y: this.panel.height + 50}, settings.panelSlideOffTime)
        .call(() => {
            this.reset();
            this.signals.prizeSequenceComplete.dispatch();
        });
    }

    freeSpinsHold(): void {

        const settings = this.services.resources.getSetting(`wheelPrizePanel`);

        Tween.get(this.panel).
        wait(settings.panelHoldTime)
        .call(() => this.signals.prizeSequenceComplete.dispatch());
    }

    hideSymbolPanel(): void {

        const settings = this.services.resources.getSetting(`wheelPrizePanel`);

        this.enableMask();

        Tween.get(this.alphaFadeTextContainer)
        .to({alpha: 0}, settings.symbolPanel.alphaFadeText.fadeOutTime)
        .call(() => this.hidePanel());
    }

    override reset(): void {

        Tween.removeTweens(this.panel);
        Tween.removeTweens(this.alphaFadeTextContainer);
        Tween.removeTweens(this.scaledTextContainer.scale);

        this.panel.y = 0;

        this.alphaFadeTextContainer.visible = false;
        this.scaledTextContainer.visible = false;

        for (const spine of this.symbols.values()) {

            spine.visible = false;

            Tween.removeTweens(spine);
            Tween.removeTweens(spine.scale);
        }

        resetSpine(this.jackpotWinBox);
        this.jackpotWinBox.visible = false;
        this.services.factory.applySpec(this.jackpotWinBox, this.spec.jackpotPanel.jackpotWinBox);

        this.disableMask();

        this.visible = false;
    }

    private setupText(spec: Record<string, any>): PrizeText {

        const {factory, resources} = this.services;

        this.alphaFadeTextContainer = factory.createContainer({visible: false}, this.panel);
        this.scaledTextContainer = this.panel.addChild(new BoundedContainer({width: 232, height: 112}));
        this.scaledTextContainer.visible = false;

        const text = {
            cashPanel: {
                line1Value: factory.createText(``, spec.cashPanel.cashPanelLine1Value),
                line1ValueBounds: new BoundedContainer(spec.cashPanel.cashPanelLine1Bounds),
                line2Text: factory.createText(resources.getMessage(`wheelPrizePanelWon`), spec.cashPanel.cashPanelLine2Text),
            },
            freeSpinsPanel: {
                line1Text: factory.createText(resources.getMessage(`wheelPrizePanelYouHaveWon`), spec.freeSpinsPanel.freeSpinsPanelLine1Text),
                line2Value: factory.createText(``, spec.freeSpinsPanel.freeSpinsPanelLine2Value),
                line2Text: factory.createText(resources.getMessage(`wheelPrizePanelFreeGames`), spec.freeSpinsPanel.freeSpinsPanelLine2Text),
                line3TextA: factory.createText(resources.getMessage(`wheelPrizePanelPlus`), spec.freeSpinsPanel.freeSpinsPanelLine3TextA),
                line3Value: factory.createText(``, spec.freeSpinsPanel.freeSpinsPanelLine3Value),
                line3ValueBounds: new BoundedContainer(spec.freeSpinsPanel.freeSpinsPanelLine3ValueBounds),
                line3TextB: factory.createText(resources.getMessage(`wheelPrizePanelWon`), spec.freeSpinsPanel.freeSpinsPanelLine3TextB),
            },
            extendFreeSpinsPanel: {
                line1Text: factory.createText(resources.getMessage(`wheelPrizePanelYouHaveWon`), spec.extendFreeSpinsPanel.extendFreeSpinsPanelLine1Text),
                line2Value: factory.createText(``, spec.extendFreeSpinsPanel.extendFreeSpinsPanelLine2Value),
                line2Text: factory.createText(resources.getMessage(`wheelPrizePanelExtraFreeGames`), spec.extendFreeSpinsPanel.extendFreeSpinsPanelLine2Text),
                line3TextA: factory.createText(resources.getMessage(`wheelPrizePanelPlus`), spec.extendFreeSpinsPanel.extendFreeSpinsPanelLine3TextA),
                line3Value: factory.createText(``, spec.extendFreeSpinsPanel.extendFreeSpinsPanelLine3Value),
                line3ValueBounds: new BoundedContainer(spec.extendFreeSpinsPanel.extendFreeSpinsPanelLine3Bounds),
                line3TextB: factory.createText(resources.getMessage(`wheelPrizePanelWon`), spec.extendFreeSpinsPanel.extendFreeSpinsPanelLine3TextB),
            },
            jackpotPanel: {
                bounds: new BoundedContainer(spec.jackpotPanel.jackpotPanelBounds),
                value: factory.createText(``, spec.jackpotPanel.jackpotPanelValue),
            },
            symbolPanel: {
                line1Text: factory.createText(resources.getMessage(`wheelPrizePanelAnd`), spec.symbolPanel.symbolPanelLine1Text, this.alphaFadeTextContainer),
                line1Separators: [] as Text[],
                line2Text: factory.createText(resources.getMessage(`wheelPrizePanelActivated`), spec.symbolPanel.symbolPanelLine2Text),
                line3TextA: factory.createText(resources.getMessage(`wheelPrizePanelPlus`), spec.symbolPanel.symbolPanelLine3TextA),
                line3Value: factory.createText(``, spec.symbolPanel.symbolPanelLine3Value),
                line3ValueBounds: new BoundedContainer(spec.symbolPanel.symbolPanelLine3ValueBounds),
                line3TextB: factory.createText(resources.getMessage(`wheelPrizePanelWon`), spec.symbolPanel.symbolPanelLine3TextB),
            }
        };

        let separator;
        for (const item of spec.symbolPanel.line1SeparatorPositions) {

            separator = factory.createText(resources.getMessage(`wheelPrizePanelSeparator`), spec.symbolPanel.symbolPanelLine1Separator, this.alphaFadeTextContainer);
            separator.x = item.x;
            separator.y = item.y;
            separator.visible = false;

            text.symbolPanel.line1Separators.push(separator);
        }


        factory.applySpec(text.cashPanel.line1ValueBounds, spec.cashPanel.cashPanelLine1Bounds);
        text.cashPanel.line1ValueBounds.addChild(text.cashPanel.line1Value);

        factory.applySpec(text.jackpotPanel.bounds, spec.jackpotPanel.jackpotPanelBounds);
        text.jackpotPanel.bounds.addChild(text.jackpotPanel.value);

        factory.applySpec(text.freeSpinsPanel.line3ValueBounds, spec.freeSpinsPanel.freeSpinsPanelLine3ValueBounds);
        text.freeSpinsPanel.line3ValueBounds.addChild(text.freeSpinsPanel.line3Value);

        factory.applySpec(text.extendFreeSpinsPanel.line3ValueBounds, spec.extendFreeSpinsPanel.extendFreeSpinsPanelLine3Bounds);
        text.extendFreeSpinsPanel.line3ValueBounds.addChild(text.extendFreeSpinsPanel.line3Value);

        factory.applySpec(text.symbolPanel.line3ValueBounds, spec.symbolPanel.symbolPanelLine3ValueBounds);
        text.symbolPanel.line3ValueBounds.addChild(text.symbolPanel.line3Value);

        return text;
    }

    private setupSymbols(): Map<CoreSymbolID, Spine> {

        const {factory, resources} = this.services;

        const spec = resources.getSpec(`symbol-spines`);
        const symbols = new Map();

        let spine;
        for (const id of this.symbolIDs) {

            spine = factory.createSpine(spec[id], this);
            spine.state.setAnimation(0,  `win-panel`, false);
            spine.visible = false;

            symbols.set(id, spine);
        }

        return symbols;
    }

    private setupMask(): void {

        const width = this.panel.width;
        const height = this.panel.height;

        this.maskGraphic = new Graphics();

        this.maskGraphic.beginFill(0xFFFFFF);
        this.maskGraphic.drawRect(-width/2, -height/2, width, height);
        this.maskGraphic.endFill();
    }

    private enableMask(): void {

        this.addChild(this.maskGraphic);
        this.mask = this.maskGraphic;
    }

    private disableMask(): void {

        this.mask = null;
        this.removeChild(this.maskGraphic);
    }

    private configureAlphaFadeTextContainer(configuration: PanelConfig): void {

        if (configuration === PanelConfig.SYMBOLS_TWO) {

            this.prizeText.symbolPanel.line1Text.x = this.spec.symbolPanel.line1TextPositions.two.x;
            this.prizeText.symbolPanel.line1Text.y = this.spec.symbolPanel.line1TextPositions.two.y;

            for (const item of this.prizeText.symbolPanel.line1Separators) {
                item.visible = false;
            }
        } else {

            this.prizeText.symbolPanel.line1Text.x = this.spec.symbolPanel.line1TextPositions.six.x;
            this.prizeText.symbolPanel.line1Text.y = this.spec.symbolPanel.line1TextPositions.six.y;

            for (const item of this.prizeText.symbolPanel.line1Separators) {
                item.visible = true;
            }
        }

        this.alphaFadeTextContainer.visible = false;
    }

    private configureScaledTextContainer(configuration: PanelConfig): void {

        this.scaledTextContainer.removeChildren();

        if (configuration === PanelConfig.CASH) {

            this.services.factory.applySpec(this.scaledTextContainer, this.spec.cashPanel.scaledTextContainer);

            this.scaledTextContainer.addChild(this.prizeText.cashPanel.line1ValueBounds);
            this.scaledTextContainer.addChild(this.prizeText.cashPanel.line2Text);

        } else if (configuration === PanelConfig.FREE_SPINS) {

            this.services.factory.applySpec(this.scaledTextContainer, this.spec.freeSpinsPanel.scaledTextContainer);

            this.scaledTextContainer.addChild(this.prizeText.freeSpinsPanel.line1Text);
            this.scaledTextContainer.addChild(this.prizeText.freeSpinsPanel.line2Value);
            this.scaledTextContainer.addChild(this.prizeText.freeSpinsPanel.line2Text);
            this.scaledTextContainer.addChild(this.prizeText.freeSpinsPanel.line3TextA);
            this.scaledTextContainer.addChild(this.prizeText.freeSpinsPanel.line3ValueBounds);
            this.scaledTextContainer.addChild(this.prizeText.freeSpinsPanel.line3TextB);

        } else if (configuration === PanelConfig.EXTEND_FREE_SPINS) {

            this.services.factory.applySpec(this.scaledTextContainer, this.spec.extendFreeSpinsPanel.scaledTextContainer);

            this.scaledTextContainer.addChild(this.prizeText.extendFreeSpinsPanel.line1Text);
            this.scaledTextContainer.addChild(this.prizeText.extendFreeSpinsPanel.line2Value);
            this.scaledTextContainer.addChild(this.prizeText.extendFreeSpinsPanel.line2Text);
            this.scaledTextContainer.addChild(this.prizeText.extendFreeSpinsPanel.line3TextA);
            this.scaledTextContainer.addChild(this.prizeText.extendFreeSpinsPanel.line3ValueBounds);
            this.scaledTextContainer.addChild(this.prizeText.extendFreeSpinsPanel.line3TextB);

        } else if (configuration === PanelConfig.SYMBOLS_TWO || configuration === PanelConfig.SYMBOLS_SIX) {

            this.services.factory.applySpec(this.scaledTextContainer, this.spec.symbolPanel.scaledTextContainer);

            this.scaledTextContainer.addChild(this.prizeText.symbolPanel.line2Text);
            this.scaledTextContainer.addChild(this.prizeText.symbolPanel.line3TextA);
            this.scaledTextContainer.addChild(this.prizeText.symbolPanel.line3ValueBounds);
            this.scaledTextContainer.addChild(this.prizeText.symbolPanel.line3TextB);
        }

        this.scaledTextContainer.update();
    }

    private playScaledTextAnim(onComplete?: () => void): void {

        const settings = this.services.resources.getSetting(`wheelPrizePanel`).scaledText;

        this.scaledTextContainer.scale.set(1);
        this.scaledTextContainer.update();
        const finalScale = this.scaledTextContainer.scale.x;

        this.scaledTextContainer.scale.set(0);
        this.scaledTextContainer.visible = true;

        const tween = Tween.get(this.scaledTextContainer.scale);
        tween.to({x: settings.scale1 * finalScale, y: settings.scale1 * finalScale}, settings.scale1Time, getEase(settings.scale1Tween));
        tween.to({x: settings.scale2 * finalScale, y: settings.scale2 * finalScale}, settings.scale2Time, getEase(settings.scale2Tween));
        tween.to({x: settings.scale3 * finalScale, y: settings.scale3 * finalScale}, settings.scale3Time, getEase(settings.scale3Tween));

        if (onComplete) {
            tween.call(onComplete);
        }
    }

    private startSymbolAnim(spine: Spine, x: number, y: number, scale: number, last: boolean): void {

        const settings = this.services.resources.getSetting(`wheelPrizePanel`).symbolPanel;

        Tween.get(spine)
        .to({alpha: 1}, settings.symbols.fadeInTime)
        .call(() =>{
            Tween.get(spine.scale)
            .to({x: scale, y: scale}, settings.symbols.scaleUpTime)
            .wait(settings.symbols.holdTime)
            .call(() => {
                const tween = Tween.get(spine);
                tween.to({x, y}, settings.symbols.moveTime);

                this.alphaFadeTextContainer.alpha = 0;
                this.alphaFadeTextContainer.visible = true;

                Tween.get(this.alphaFadeTextContainer)
                .to({alpha: 1}, settings.alphaFadeText.fadeInTime);

                if (last) {
                    tween.call(() => {
                        this.playScaledTextAnim();
                        this.signals.startSymbolCollection.dispatch();
                    });
                }
            });
        });
    }
}